import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Extracts bucket and path from a Supabase storage URL.
 * Handles both /object/public/ and /object/authenticated/ formats.
 *
 * EXPORTED FOR THE ONE OTHER CALLER THAT SIGNS A STORED URL RATHER THAN OPENING IT.
 * `certificateOnFile` fetches the filed certificate's bytes instead of handing the tab a link, and
 * a second copy of this regex is a second place the `/object/public/` and `/object/authenticated/`
 * spellings — and the `decodeURIComponent` that follows them — could drift apart.
 */
export function parseStorageUrl(url: string): { bucket: string; path: string } | null {
    try {
        const match = url.match(/\/storage\/v1\/object\/(?:public|authenticated)\/([^/]+)\/(.+)/);
        if (match) {
            return { bucket: match[1], path: decodeURIComponent(match[2]) };
        }
    } catch {
        // ignore parse errors
    }
    return null;
}

/**
 * Opens a storage file in a new tab using a signed URL.
 * Parses the Supabase storage URL, generates a signed URL, and opens it.
 *
 * Passthrough: if the URL is not a Supabase storage URL (e.g. external links),
 * it is opened directly without signing.
 */
export async function openSignedUrl(storageUrl: string | null | undefined): Promise<void> {
    if (!storageUrl) return;

    // Not a storage URL — open directly
    if (!storageUrl.includes('/storage/v1/object/')) {
        window.open(storageUrl, '_blank');
        return;
    }

    const parsed = parseStorageUrl(storageUrl);
    if (!parsed) {
        window.open(storageUrl, '_blank');
        return;
    }

    const { data, error } = await supabase.storage
        .from(parsed.bucket)
        .createSignedUrl(parsed.path, 3600);

    if (error || !data?.signedUrl) {
        toast.error('Failed to open file');
        return;
    }

    window.open(data.signedUrl, '_blank');
}

/**
 * Opens a storage object in a new tab, given the two halves directly.
 *
 * For callers that already hold a bucket and a path rather than a URL —
 * `get_agreement_documents` reports the executed PDF as `final_bucket` + `final_path`. Composing
 * those into a storage URL just so `openSignedUrl` can take it apart again would put an
 * unencoded path through a parse that decodes it; this signs the object itself.
 */
export async function openStorageObject(bucket: string, path: string): Promise<void> {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);

    if (error || !data?.signedUrl) {
        toast.error('Failed to open file');
        return;
    }

    window.open(data.signedUrl, '_blank');
}
